<template>
  <div class="logs-container">
    <FloatingHeader
      title="Logs"
      sbtitle="Arquivos do servidor"
      :showHeader="true"
      routeToBack="/"
      :hideSearch="true"
      :goBackOrHome="true"
    ></FloatingHeader>

    <div class="logs-toolbar">
      <div class="breadcrumb">
        <button
          v-for="item in breadcrumbs"
          :key="item.path || 'root'"
          type="button"
          @click="openDirectory(item.path)"
        >
          {{ item.name }}
        </button>
      </div>

      <button type="button" class="refresh-button" @click="loadDirectory(currentPath)">
        Atualizar
      </button>
    </div>

    <p v-if="errorMessage" class="feedback error">{{ errorMessage }}</p>
    <p v-else-if="isLoading" class="feedback">Carregando logs...</p>

    <div v-else class="logs-layout">
      <section class="logs-list">
        <button
          v-if="parentPath !== null"
          type="button"
          class="entry directory"
          @click="openDirectory(parentPath)"
        >
          <span class="entry-name">..</span>
          <span class="entry-meta">Voltar</span>
        </button>

        <button
          v-for="entry in entries"
          :key="entry.path"
          type="button"
          class="entry"
          :class="{ directory: entry.type === 'directory', active: selectedFile && selectedFile.path === entry.path }"
          @click="openEntry(entry)"
        >
          <span class="entry-name">{{ entry.name }}</span>
          <span class="entry-meta">{{ formatEntryMeta(entry) }}</span>
        </button>

        <p v-if="entries.length === 0" class="empty-message">Nenhum log encontrado nessa pasta.</p>
      </section>

      <section class="log-preview">
        <div class="preview-header">
          <div>
            <h2>{{ selectedFile ? selectedFile.name : 'Selecione um arquivo' }}</h2>
            <span v-if="selectedFile">{{ formatFileSize(selectedFile.size) }} - {{ formatDate(selectedFile.modifiedAt) }}</span>
          </div>

          <select v-model.number="tail" @change="reloadSelectedFile">
            <option :value="500">500 linhas</option>
            <option :value="2000">2000 linhas</option>
            <option :value="5000">5000 linhas</option>
            <option :value="10000">10000 linhas</option>
          </select>
        </div>

        <pre v-if="fileContent">{{ fileContent }}</pre>
        <p v-else class="empty-message">O conteudo do log aparece aqui.</p>
      </section>
    </div>
  </div>
</template>

<script>
import FloatingHeader from '@/components/FloatingHeader.vue'
import requestModule from '@/assets/libs/requestModule'

export default {
  name: 'LogsView',
  components: {
    FloatingHeader
  },
  data () {
    return {
      breadcrumbs: [{ name: 'logs', path: '' }],
      currentPath: '',
      entries: [],
      selectedFile: null,
      fileContent: '',
      isLoading: false,
      errorMessage: '',
      tail: 2000
    }
  },
  computed: {
    parentPath () {
      if (!this.currentPath) {
        return null
      }

      const parts = this.currentPath.split('/').filter(Boolean)
      parts.pop()
      return parts.join('/')
    }
  },
  mounted () {
    const path = this.$route.query.path || ''
    this.loadDirectory(path)
  },
  methods: {
    async loadDirectory (path = '') {
      this.isLoading = true
      this.errorMessage = ''
      this.selectedFile = null
      this.fileContent = ''

      try {
        const response = await requestModule.postRequest(`/support/logs?path=${encodeURIComponent(path)}`, {}, null, 'get')

        if (response.error) {
          throw new Error(response.message)
        }

        this.currentPath = response.data.currentPath
        this.breadcrumbs = response.data.breadcrumbs
        this.entries = response.data.entries
        this.$router.replace({ path: '/logs', query: this.currentPath ? { path: this.currentPath } : {} })
      } catch (error) {
        this.errorMessage = this.getErrorMessage(error)
      } finally {
        this.isLoading = false
      }
    },
    openDirectory (path) {
      this.loadDirectory(path)
    },
    openEntry (entry) {
      if (entry.type === 'directory') {
        this.openDirectory(entry.path)
        return
      }

      this.loadFile(entry)
    },
    async loadFile (entry) {
      this.errorMessage = ''
      this.selectedFile = entry
      this.fileContent = 'Carregando arquivo...'

      try {
        const query = `path=${encodeURIComponent(entry.path)}&tail=${this.tail}`
        const response = await requestModule.postRequest(`/support/logs/file?${query}`, {}, null, 'get')

        if (response.error) {
          throw new Error(response.message)
        }

        this.fileContent = response.data.content || ''
      } catch (error) {
        this.fileContent = ''
        this.errorMessage = this.getErrorMessage(error)
      }
    },
    reloadSelectedFile () {
      if (this.selectedFile) {
        this.loadFile(this.selectedFile)
      }
    },
    formatEntryMeta (entry) {
      if (entry.type === 'directory') {
        return `Pasta - ${this.formatDate(entry.modifiedAt)}`
      }

      return `${this.formatFileSize(entry.size)} - ${this.formatDate(entry.modifiedAt)}`
    },
    formatFileSize (size) {
      if (!size) {
        return '0 B'
      }

      if (size < 1024) {
        return `${size} B`
      }

      if (size < 1024 * 1024) {
        return `${(size / 1024).toFixed(1)} KB`
      }

      return `${(size / 1024 / 1024).toFixed(1)} MB`
    },
    formatDate (date) {
      if (!date) {
        return ''
      }

      return new Date(date).toLocaleString('pt-BR')
    },
    getErrorMessage (error) {
      return error?.response?.data?.message || error?.message || 'Falha ao carregar logs.'
    }
  }
}
</script>

<style lang="less" scoped>
.logs-container {
  padding: 88px 16px 32px 16px;
  min-height: calc(var(--real-vh) - 120px);
  background: #f5f7fb;
  color: #18202f;
}

.logs-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
}

.breadcrumb {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;

  button {
    border: 1px solid #d5dbe7;
    background: #ffffff;
    color: #18202f;
    border-radius: 6px;
    padding: 8px 10px;
    font-size: 13px;
    cursor: pointer;
  }
}

.refresh-button {
  border: none;
  background: var(--primaryColor);
  color: #ffffff;
  border-radius: 6px;
  padding: 9px 14px;
  font-size: 13px;
  cursor: pointer;
}

.feedback {
  margin: 18px 0;
  color: #46536b;

  &.error {
    color: #b42318;
  }
}

.logs-layout {
  display: grid;
  grid-template-columns: minmax(260px, 36%) minmax(0, 1fr);
  gap: 14px;
}

.logs-list,
.log-preview {
  min-height: 480px;
  background: #ffffff;
  border: 1px solid #dde3ee;
  border-radius: 8px;
  overflow: hidden;
}

.logs-list {
  display: flex;
  flex-direction: column;
}

.entry {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: center;
  gap: 10px;
  width: 100%;
  border: none;
  border-bottom: 1px solid #edf0f5;
  background: #ffffff;
  color: #18202f;
  padding: 12px;
  text-align: left;
  cursor: pointer;

  &.directory .entry-name {
    font-weight: 700;
  }

  &.active,
  &:hover {
    background: #eef5ff;
  }
}

.entry-name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 14px;
}

.entry-meta {
  color: #667085;
  font-size: 12px;
  white-space: nowrap;
}

.log-preview {
  display: flex;
  flex-direction: column;
}

.preview-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  min-height: 62px;
  padding: 12px;
  border-bottom: 1px solid #edf0f5;

  h2 {
    margin: 0 0 4px 0;
    font-size: 16px;
  }

  span {
    color: #667085;
    font-size: 12px;
  }

  select {
    border: 1px solid #d5dbe7;
    border-radius: 6px;
    background: #ffffff;
    padding: 8px;
    font-size: 13px;
  }
}

pre {
  flex: 1;
  margin: 0;
  padding: 14px;
  overflow: auto;
  background: #101828;
  color: #e4e7ec;
  font-size: 12px;
  line-height: 1.5;
  white-space: pre-wrap;
  word-break: break-word;
}

.empty-message {
  margin: 0;
  padding: 16px;
  color: #667085;
}

@media (max-width: 760px) {
  .logs-layout {
    grid-template-columns: 1fr;
  }

  .logs-toolbar,
  .preview-header {
    align-items: stretch;
    flex-direction: column;
  }
}
</style>
